import type { IntlShape } from "react-intl"

import { ClusterTypes } from "@/domain/cluster/constants"

// Nearby agency suggestions of the recipients step (ADR 0004, D4; ADR 0017, D3).
export const NEARBY_SUGGESTIONS_COUNT = 5

// Agencies dedicated to an activity (ADR 0017, D1), by the codes of the "project:activity:"
// labels. These ids come from BarnesDatas and are the same in every environment.
const SPECIALISED_CLUSTER_IDS: Record<string, string> = {
  art_and_design: "885a719a-cf6b-4b2e-b454-0e1a479240ed", // BARNES Art & Design
  real_estate_asset_management: "77456ef4-9bfa-4915-b752-2e3d880e556f", // BARNES Asset Management
  club_collection_co_ownership: "80ba1dc1-f887-59cd-97f2-68ed0a4033c9", // BARNES Collection Club
  concierge_services: "dd0846ec-f026-494b-9d5e-c843234ce1d2", // BARNES Global Office
  fine_violins: "1ef93040-fde0-6aac-95eb-0bc8b052cee1", // BARNES Fine Violins
  fine_wines: "d46c3f14-5604-5000-a482-e85735ecfeee", // BARNES Fine Wines
  wealth_management: "34612f0e-1db4-4c72-ae05-bd88ec728e66", // BARNES Family Office
  real_estate_valuation: "1f0d782d-d052-6c54-a433-adb92f15b5d6", // BARNES Valuation
  cars: "1f0d7621-9576-6ba2-b3c1-a127a1d97fef", // BARNES CarJager
  yachting: "ef3bf9b4-3d76-48f3-9970-f53e7da10e04", // BARNES Yachting
}

// Architecture & decoration: the agency of the property's country, none elsewhere.
const INTERIORS_CLUSTER_IDS: Record<string, string> = {
  FR: "1efac1e0-e72d-6494-aee5-5302644733f8", // BARNES Interiors France
  ES: "9e26770c-d335-4188-be8d-cf7fc825f3b5", // BARNES Interiors Madrid
}

export type SpecialisedClustersFilter = { "id[]": string[] } | { "type.code": string }

// The GET /clusters filter of the agencies dedicated to the activity, or undefined when the
// activity has none and the nearest agencies are suggested (ADR 0017, D1 and D2).
export const specialisedClustersFor = (
  activity?: string | null,
  country?: string | null
): SpecialisedClustersFilter | undefined => {
  if (activity === "hospitality") return { "type.code": ClusterTypes.HOSPITALITY }
  if (activity === "architecture_and_decoration") {
    const id = INTERIORS_CLUSTER_IDS[(country ?? "").toUpperCase()]
    return { "id[]": id === undefined ? [] : [id] }
  }
  const id = SPECIALISED_CLUSTER_IDS[activity ?? ""]
  return id === undefined ? undefined : { "id[]": [id] }
}

// The agencies dedicated to the activity, whatever their distance, without the sender agency.
export const pickSpecialisedSuggestions = <T extends { id?: string }>(
  clusters: T[],
  excludedClusterId?: string | null
): T[] => clusters.filter(cluster => cluster.id !== excludedClusterId)

type ClusterWithDistance = { id?: string; distance?: number | null }

// The nearest clusters that have coordinates, without the sender agency. The API already sorts
// them by distance (GET /clusters?near[lat]&near[lng]); clusters without coordinates come last
// with a null distance and are never suggested.
export const pickNearbySuggestions = <T extends ClusterWithDistance>(
  clusters: T[],
  excludedClusterId?: string | null
): T[] =>
  clusters
    .filter(cluster => typeof cluster.distance === "number" && cluster.id !== excludedClusterId)
    .slice(0, NEARBY_SUGGESTIONS_COUNT)

const NBSP = " "

// Always in km: one decimal under 10 km ("2,5 km"), none above ("12 km").
export const formatDistanceKm = (intl: IntlShape, km: number) =>
  `${intl.formatNumber(km, { maximumFractionDigits: km < 10 ? 1 : 0 })}${NBSP}km`
